import { useForm as useHookForm } from 'react-hook-form';
import * as Yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useTranslation } from 'react-i18next';

import { CATEGORIES_ID, REGEX } from 'src/constants/app';
import { allowNaNNumber } from 'src/utils/helper';
import { formatSnpoGuide } from 'src/utils/quotation';

export type FormItem = {
  variety: string;
  shape: string;
  product_machining: string;
  product_thickness: string;
  product_width: string;
  product_length: string;
  dimension_4: string;
  dimension_5: string;
  dimension_6: string;
  converted_product_thickness?: string;
  converted_product_width?: string;
  converted_product_length?: string;
  converted_dimension_4?: string;
  converted_dimension_5?: string;
  converted_dimension_6?: string;
  number_product: string;
  machining_thickness: string;
  machining_width: string;
  machining_length: string;
  machining_outside_diameter: string;
  machining_inside_diameter: string;
  mill_sheets?: string;
  order_notes: string;
  // data optional
  productItem?: Response.ProductItem;
  machiningItem?: {
    label: string;
    value: string;
  };
  isProductWidthDisabled?: boolean;
  isProductLengthDisabled?: boolean;
  isDimension4Disabled?: boolean;
  isDimension5Disabled?: boolean;
  isDimension6Disabled?: boolean;
  sizeItem: any;
  chamferItem: any;
  seihnJoho?: string;
  industry?: string;
  region?: string;
  product?: string;
  isImportedProductOnlyAndZ0?: boolean;
  isProductFinishedFlag?: boolean;
  toleranceOptions: Response.ToleranceGroupOptions;
  isResetByMachiningMethod: boolean;
  chamferMethod: Response.ChamferItem | null;
  isResetByProductSelection: boolean;
  isShowMachiningTolerance: boolean;
  numberOfInputDimensions: string;
  isSwapDimension: boolean;
  decimalInputSizeFlags: Payload.DecimalInputSizeFlags;
  showProcessingRange: string[];
  inputAutoFillByType: string;
};

export type FormValue = {
  customer_name: string;
  destination: string;
  destinationItem?: any;
  priceQuotes: FormItem[];
};

const useForm = () => {
  const { t } = useTranslation();

  const priceQuoteSchema = Yup.object().shape({
    // variety: Yup.string().required(),
    // shape: Yup.string().required(),
    product_thickness: Yup.string()
      .nullable()
      .test({
        name: 'product_thickness_required',
        test(value) {
          const sizeItem = this.parent?.sizeItem;
          const sizeInputLabel = formatSnpoGuide(
            sizeItem?.snpoGuide,
            sizeItem?.snpoSize,
          );
          const headLabel = sizeInputLabel[0] || '';
          if (this.parent?.isProductFinishedFlag) {
            return true;
          }
          if (!value) {
            return this.createError({
              message: t('message.E000007', {
                0: headLabel,
              }),
            });
          }
          if (!REGEX.REGEX_PRODUCT_THICKNESS.test(value)) {
            return this.createError({
              message: t('message.E000012', {
                0: headLabel,
              }),
            });
          }
          return true;
        },
      }),
    seihnJoho: Yup.string()
      .nullable()
      .test({
        name: 'seihnJoho_required_when_finished',
        test(value) {
          if (!this.parent?.isProductFinishedFlag) {
            return true;
          }
          if (value) {
            return true;
          }
          return this.createError({
            message: t('message.E000007', {
              0: t('pricing_page.type_selection'),
            }),
          });
        },
      }),
    product_width: Yup.string()
      .nullable()
      .test({
        name: 'product_width_required',
        test(value) {
          const sizeItem = this.parent?.sizeItem;
          const isWidthDisabled = this.parent?.isProductWidthDisabled;
          const sizeInputLabel = formatSnpoGuide(
            sizeItem?.snpoGuide,
            sizeItem?.snpoSize,
          );
          const widthLabel = sizeInputLabel[1] || '';
          if (
            isWidthDisabled ||
            this.parent?.isProductFinishedFlag ||
            this.parent?.isImportedProductOnlyAndZ0 ||
            !widthLabel
          ) {
            return true;
          }
          if (!value) {
            return this.createError({
              message: t('message.E000003', {
                0: widthLabel,
              }),
            });
          }
          return true;
        },
      }),
    product_length: Yup.string()
      .nullable()
      .test({
        name: 'product_length_required',
        test(value) {
          const sizeItem = this.parent?.sizeItem;
          const isLengthDisabled = this.parent?.isProductLengthDisabled;
          const sizeInputLabel = formatSnpoGuide(
            sizeItem?.snpoGuide,
            sizeItem?.snpoSize,
          );
          const lengthLabel = sizeInputLabel[2] || '';
          if (
            isLengthDisabled ||
            this.parent?.isProductFinishedFlag ||
            this.parent?.isImportedProductOnlyAndZ0 ||
            !lengthLabel
          ) {
            return true;
          }
          if (!value) {
            return this.createError({
              message: t('message.E000003', {
                0: lengthLabel,
              }),
            });
          }
          return true;
        },
      }),
    dimension_4: Yup.string()
      .nullable()
      .test({
        name: 'dimension_4_required',
        test(value) {
          const sizeItem = this.parent?.sizeItem;
          const isDimension4Disabled = this.parent?.isDimension4Disabled;
          const sizeInputLabel = formatSnpoGuide(
            sizeItem?.snpoGuide,
            sizeItem?.snpoSize,
          );
          const dimension4Label = sizeInputLabel[3] || '';
          if (
            isDimension4Disabled ||
            this.parent?.isProductFinishedFlag ||
            this.parent?.isImportedProductOnlyAndZ0 ||
            !dimension4Label
          ) {
            return true;
          }
          if (!value) {
            return this.createError({
              message: t('message.E000003', {
                0: dimension4Label,
              }),
            });
          }
          return true;
        },
      }),
    dimension_5: Yup.string()
      .nullable()
      .test({
        name: 'dimension_5_required',
        test(value) {
          const sizeItem = this.parent?.sizeItem;
          const isDimension5Disabled = this.parent?.isDimension5Disabled;
          const sizeInputLabel = formatSnpoGuide(
            sizeItem?.snpoGuide,
            sizeItem?.snpoSize,
          );
          const dimension5Label = sizeInputLabel[4] || '';
          if (
            isDimension5Disabled ||
            this.parent?.isProductFinishedFlag ||
            this.parent?.isImportedProductOnlyAndZ0 ||
            !dimension5Label
          ) {
            return true;
          }
          if (!value) {
            return this.createError({
              message: t('message.E000003', {
                0: dimension5Label,
              }),
            });
          }
          return true;
        },
      }),
    dimension_6: Yup.string()
      .nullable()
      .test({
        name: 'dimension_6_required',
        test(value) {
          const sizeItem = this.parent?.sizeItem;
          const isDimension6Disabled = this.parent?.isDimension6Disabled;
          const sizeInputLabel = formatSnpoGuide(
            sizeItem?.snpoGuide,
            sizeItem?.snpoSize,
          );
          const dimension6Label = sizeInputLabel[5] || '';
          if (
            isDimension6Disabled ||
            this.parent?.isProductFinishedFlag ||
            this.parent?.isImportedProductOnlyAndZ0 ||
            !dimension6Label
          ) {
            return true;
          }
          if (!value) {
            return this.createError({
              message: t('message.E000003', {
                0: dimension6Label,
              }),
            });
          }
          return true;
        },
      }),
    number_product: Yup.number()
      .required(
        t('message.E000007', {
          0: t('pricing_page.product_size_field.number_field'),
        }),
      )
      .min(
        1,
        t('message.E000009', {
          0: t('pricing_page.product_size_field.number_field'),
          1: '1',
        }),
      )
      .transform(allowNaNNumber)
      .nullable(),
    machining_thickness: Yup.string().nullable(),
    machining_width: Yup.string().nullable(),
    machining_length: Yup.string().nullable(),
    machining_outside_diameter: Yup.string().nullable(),
    machining_inside_diameter: Yup.string().nullable(),
    order_notes: Yup.string().nullable(),
    product_machining: Yup.string().nullable(),
    mill_sheets: Yup.string().nullable(),
    converted_product_thickness: Yup.string().nullable(),
    converted_product_width: Yup.string().nullable(),
    converted_product_length: Yup.string().nullable(),
    sizeItem: Yup.mixed().nullable(),
    chamferItem: Yup.mixed().nullable(),
    productItem: Yup.mixed().nullable(),
    machiningItem: Yup.mixed()
      .nullable()
      .test({
        name: 'machiningItem_required',
        test(value, context) {
          // Get variety from the current item's context
          const variety = context.parent?.variety;

          // Do not validate if variety is TOOL_PRODUCTS (07)
          if (variety === CATEGORIES_ID.TOOL_PRODUCTS) {
            return true;
          }

          // Validate machiningItem when variety exists and is not TOOL_PRODUCTS
          if (!value || typeof value !== 'object' || !value.value) {
            return context.createError({
              message: t('message.E000007', {
                0: t('pricing_page.machining_method_field'),
              }),
            });
          }

          return true;
        },
      }),
    industry: Yup.string().nullable(),
    region: Yup.string().nullable(),
  });

  const schema = Yup.object().shape({
    destination: Yup.string().required(
      t('message.E000007', {
        0: t(`label.destination`),
      }),
    ),
    priceQuotes: Yup.array().of(priceQuoteSchema),
  });

  const formInfo = useHookForm<FormValue>({
    resolver: yupResolver(schema),
    shouldFocusError: true,
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    defaultValues: {
      priceQuotes: [],
    },
  });
  return { formInfo };
};

export default useForm;
